// src/pages/AdminGoldRequests.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import DateObject from 'react-date-object';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Calendar as CalendarIcon, 
  ArrowLeft,
  User,
  Package,
  CheckCircle,
  XCircle,
  Copy,
  AlertCircle,
  X,
  Scale,
  BadgeCheck,
  Building
} from 'lucide-react';
import Swal from 'sweetalert2';

// نمایش پیام
const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

// فرمت اعداد
const formatNumber = (value, isGold = false) => {
  if (!value || isNaN(parseFloat(value))) return '0';
  const number = parseFloat(value);
  return isGold
    ? number.toLocaleString('fa-IR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : number.toLocaleString('fa-IR');
};

// تبدیل اعداد فارسی به لاتین
const convertToLatinDigits = (input) => {
  if (!input) return input;
  const persianDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
  const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
  let result = input;
  for (let i = 0; i < 10; i++) {
    result = result.replace(persianDigits[i], i).replace(arabicDigits[i], i);
  }
  return result;
};

// تبدیل تاریخ
const formatDateForBackend = (date, isEndDate = false) => {
  if (!date) return date;
  try {
    const latinDate = convertToLatinDigits(date);
    const dateObject = new DateObject({ date: latinDate, calendar: persian, locale: persian_fa });
    const gregorianDate = dateObject.toDate();
    if (isEndDate) gregorianDate.setHours(23, 59, 59, 999);
    else gregorianDate.setHours(0, 0, 0, 0);
    return gregorianDate.toISOString().replace(/\.\d{3}Z$/, '');
  } catch (err) {
    return date;
  }
};

const formatToPersianDate = (date) => {
  if (!date) return '-';
  try {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return '-';
    const dateObject = new DateObject({ date: parsedDate, calendar: persian, locale: persian_fa });
    return dateObject.format('YYYY/MM/DD HH:mm');
  } catch (err) {
    return '-';
  }
};

export default function AdminGoldRequests() {
  const [requests, setRequests] = useState([]);
  const [rejectReason, setRejectReason] = useState({});
  const [filters, setFilters] = useState({ userSearch: '', status: '', startDate: '', endDate: '' });
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target)) setShowStartCalendar(false);
      if (endCalendarRef.current && !endCalendarRef.current.contains(event.target)) setShowEndCalendar(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchRequests = async (appliedFilters) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/gold/admin-requests', {
        headers: { Authorization: `Bearer ${newToken}` },
        params: {
          ...appliedFilters,
          startDate: formatDateForBackend(appliedFilters.startDate, false),
          endDate: formatDateForBackend(appliedFilters.endDate, true),
        },
      });
      setRequests(res.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'خطا در دریافت درخواست‌ها';
      showAlert(errorMessage, 'error');
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchRequests(filters);
  }, [token, navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    if (date) {
      setTempFilters((prev) => ({ ...prev, [field]: date.format('YYYY/MM/DD') }));
    } else {
      setTempFilters((prev) => ({ ...prev, [field]: '' }));
    }
    if (field === 'startDate') setShowStartCalendar(false);
    if (field === 'endDate') setShowEndCalendar(false);
  };

  const getCalendarValue = (date) => {
    try {
      if (date) {
        return new DateObject({
          date: convertToLatinDigits(date),
          calendar: persian,
          locale: persian_fa,
          format: 'YYYY/MM/DD',
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (tempFilters.startDate && tempFilters.endDate) {
      const start = new DateObject({ date: convertToLatinDigits(tempFilters.startDate), calendar: persian, locale: persian_fa }).toDate();
      const end = new DateObject({ date: convertToLatinDigits(tempFilters.endDate), calendar: persian, locale: persian_fa }).toDate();
      if (start > end) {
        showAlert('تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد', 'error');
        return;
      }
    }
    setFilters(tempFilters);
    fetchRequests(tempFilters);
    setShowFiltersMobile(false);
  };

  const handleResetFilters = () => {
    const reset = { userSearch: '', status: '', startDate: '', endDate: '' };
    setTempFilters(reset);
    setFilters(reset);
    fetchRequests(reset);
    setShowFiltersMobile(false);
  };

  const handleUpdate = async (id, status) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const body = { status };
      if (status === 'rejected') {
        body.reject_reason = rejectReason[id] || 'دلیل رد مشخص نشده';
      }
      const res = await api.put(`/api/gold/requests/${id}`, body, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      showAlert(res.data.message, 'success');
      fetchRequests(filters);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'خطا در به‌روزرسانی درخواست';
      showAlert(errorMessage, 'error');
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار';
      case 'approved': return 'تأیید شده';
      case 'ready': return 'آماده تحویل';
      case 'delivered': return 'تحویل داده شده';
      case 'rejected': return 'رد شده';
      case 'canceled': return 'لغو شده';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'approved': return 'bg-green-100 border-green-300 text-green-800';
      case 'ready': return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'delivered': return 'bg-gray-100 border-gray-300 text-gray-800';
      case 'rejected': return 'bg-red-100 border-red-300 text-red-800';
      case 'canceled': return 'bg-gray-100 border-gray-300 text-gray-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showAlert('کپی شد!', 'success');
    }).catch(() => {
      showAlert('خطا در کپی', 'error');
    });
  };

  const AngCopy = ({ value }) => {
    if (!value) return null;
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg text-xs">
        <span className="font-mono text-gray-800">{value}</span>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => copyToClipboard(value)}
          className="text-indigo-600"
        >
          <Copy className="w-3.5 h-3.5" />
        </motion.button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 px-4 py-8 font-vazir">
      <div className="max-w-7xl mx-auto">

        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-amber-700 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Package className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">مدیریت درخواست‌های آبشده</h2>
          <p className="text-gray-600 mt-2">بررسی و تأیید/رد درخواست‌های طلای آبشده</p>
        </motion.div>

        {/* دکمه فیلتر موبایل */}
        <div className="sm:hidden mb-6">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white py-3 px-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg">
            <Filter className="w-5 h-5" />
            فیلترها
          </motion.button>
        </div>

        {/* فیلترها */}
        <AnimatePresence>
          {(showFiltersMobile || !isMobile) && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 mb-8 border border-white/30">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <Filter className="w-6 h-6 text-amber-600" />
                  فیلترهای پیشرفته
                </h3>
                {isMobile && (
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setShowFiltersMobile(false)} className="text-gray-500">
                    <X className="w-6 h-6" />
                  </motion.button>
                )}
              </div>
              <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">جستجوی کاربر</label>
                  <div className="relative">
                    <input type="text" name="userSearch" value={tempFilters.userSearch} onChange={handleFilterChange}
                      placeholder="نام یا شناسه"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">وضعیت</label>
                  <select name="status" value={tempFilters.status} onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="">همه</option>
                    <option value="pending">در انتظار</option>
                    <option value="approved">تأیید شده</option>
                    <option value="ready">آماده تحویل</option>
                    <option value="delivered">تحویل داده شده</option>
                    <option value="rejected">رد شده</option>
                    <option value="canceled">لغو شده</option>
                  </select>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">از تاریخ</label>
                  <div className="flex items-center gap-2">
                    <input type="text" name="startDate" value={tempFilters.startDate} onChange={handleFilterChange}
                      placeholder="1404/01/01"
                      className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} type="button"
                      onClick={() => setShowStartCalendar(!showStartCalendar)}
                      className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl shadow-lg">
                      <CalendarIcon className="w-5 h-5" />
                    </motion.button>
                  </div>
                  {showStartCalendar && (
                    <motion.div ref={startCalendarRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border">
                      <Calendar calendar={persian} locale={persian_fa} value={getCalendarValue(tempFilters.startDate)}
                        onChange={(date) => handleDateChange(date, 'startDate')} className="rmdp-mobile" />
                    </motion.div>
                  )}
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">تا تاریخ</label>
                  <div className="flex items-center gap-2">
                    <input type="text" name="endDate" value={tempFilters.endDate} onChange={handleFilterChange}
                      placeholder="1404/01/01"
                      className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} type="button"
                      onClick={() => setShowEndCalendar(!showEndCalendar)}
                      className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl shadow-lg">
                      <CalendarIcon className="w-5 h-5" />
                    </motion.button>
                  </div>
                  {showEndCalendar && (
                    <motion.div ref={endCalendarRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border">
                      <Calendar calendar={persian} locale={persian_fa} value={getCalendarValue(tempFilters.endDate)}
                        onChange={(date) => handleDateChange(date, 'endDate')} className="rmdp-mobile" />
                    </motion.div>
                  )}
                </div>
                <div className="flex gap-3 items-end">
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2">
                    <Filter className="w-5 h-5" /> اعمال
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleResetFilters}
                    className="flex-1 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2">
                    <XCircle className="w-5 h-5" /> پاک کردن
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* جدول */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30 overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <p className="text-lg font-semibold text-gray-700">
              تعداد درخواست‌ها: <span className="text-amber-600">{requests.length}</span>
            </p>
          </div>

          {/* دسکتاپ */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-yellow-50 to-orange-50 text-gray-700">
                  <th className="p-3 text-right font-bold">شناسه</th>
                  <th className="p-3 text-right font-bold">کاربر</th>
                  <th className="p-3 text-right font-bold">وزن (گرم)</th>
                  <th className="p-3 text-right font-bold">عیار</th>
                  <th className="p-3 text-right font-bold">وزن 750</th>
                  <th className="p-3 text-right font-bold">شماره انگ</th>
                  <th className="p-3 text-right font-bold">آزمایشگاه</th>
                  <th className="p-3 text-right font-bold">وضعیت</th>
                  <th className="p-3 text-right font-bold">دلیل رد</th>
                  <th className="p-3 text-right font-bold">تاریخ</th>
                  <th className="p-3 text-right font-bold">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <motion.tr key={req.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} whileHover={{ backgroundColor: '#f8fafc' }} className="border-b">
                    <td className="p-3 font-mono text-amber-600">#{req.id}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{req.first_name} {req.last_name}</p>
                          <p className="text-xs text-gray-500">ID: {req.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-gray-900">{formatNumber(req.vazn, true)}</td>
                    <td className="p-3 font-mono text-gray-900">{formatNumber(req.ayar)}</td>
                    <td className="p-3 font-mono text-gray-900">{formatNumber(req.vazn750, true)}</td>
                    <td className="p-3"><AngCopy value={req.ang} /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Building className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-xs">{req.rey || '—'}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(req.status)}`}>
                        {getStatusText(req.status)}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-600">{req.reject_reason || '—'}</td>
                    <td className="p-3 text-xs font-mono text-gray-700">{formatToPersianDate(req.created_at)}</td>
                    <td className="p-3">
                      {['pending', 'approved', 'ready'].includes(req.status) && (
                        <div className="flex flex-col gap-1">
                          {req.status === 'pending' && (
                            <button onClick={() => handleUpdate(req.id, 'approved')}
                              className="bg-green-500 hover:bg-green-600 text-white py-1.5 px-3 rounded-lg font-medium text-xs transition-all shadow-sm hover:shadow-md">
                              تأیید
                            </button>
                          )}
                          {(req.status === 'approved' || req.status === 'ready') && (
                            <button onClick={() => handleUpdate(req.id, 'delivered')}
                              className="bg-blue-500 hover:bg-blue-600 text-white py-1.5 px-3 rounded-lg font-medium text-xs transition-all shadow-sm hover:shadow-md">
                              تحویل داده شد
                            </button>
                          )}
                          <div className="flex gap-1">
                            <input type="text" placeholder="دلیل رد" value={rejectReason[req.id] || ''}
                              onChange={(e) => setRejectReason({ ...rejectReason, [req.id]: e.target.value })}
                              className="flex-1 p-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-red-500" />
                            <button onClick={() => handleUpdate(req.id, 'rejected')}
                              className="bg-red-500 hover:bg-red-600 text-white py-1.5 px-2 rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md">
                              رد
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* موبایل */}
          <div className="lg:hidden divide-y divide-gray-200">
            {requests.map((req) => (
              <motion.div key={req.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} whileHover={{ backgroundColor: '#f8fafc' }}
                className={`p-3 ${getStatusColor(req.status)}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="bg-white px-2 py-0.5 rounded-full text-xs font-bold text-gray-800">#{req.id}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusColor(req.status)}`}>
                    {getStatusText(req.status)}
                  </span>
                </div>

                <div className="mb-2">
                  <p className="font-bold text-base text-gray-900 mb-1">
                    وزن: {formatNumber(req.vazn, true)} گرم
                  </p>
                  <p className="text-sm text-gray-600">{req.first_name} {req.last_name}</p>
                  <p className="text-xs text-gray-500">ID: {req.user_id}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>عیار: <span className="font-mono">{formatNumber(req.ayar)}</span></div>
                  <div>وزن 750: <span className="font-mono">{formatNumber(req.vazn750, true)}</span></div>
                  <div>شماره انگ: <AngCopy value={req.ang} /></div>
                  <div>آزمایشگاه: <span>{req.rey || '—'}</span></div>
                </div>

                <div className="text-xs text-gray-600 mb-2">
                  تاریخ: {formatToPersianDate(req.created_at)}
                </div>

                {['pending', 'approved', 'ready'].includes(req.status) && (
                  <div className="space-y-1 pt-2 border-t border-gray-200">
                    {req.status === 'pending' && (
                      <button onClick={() => handleUpdate(req.id, 'approved')}
                        className="w-full bg-green-500 hover:bg-green-600 text-white py-2.5 px-3 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-all">
                        تأیید
                      </button>
                    )}
                    {(req.status === 'approved' || req.status === 'ready') && (
                      <button onClick={() => handleUpdate(req.id, 'delivered')}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2.5 px-3 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-all">
                        تحویل داده شد
                      </button>
                    )}
                    <div className="flex gap-1">
                      <input type="text" placeholder="دلیل رد" value={rejectReason[req.id] || ''}
                        onChange={(e) => setRejectReason({ ...rejectReason, [req.id]: e.target.value })}
                        className="flex-1 p-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-red-500" />
                      <button onClick={() => handleUpdate(req.id, 'rejected')}
                        className="bg-red-500 hover:bg-red-600 text-white py-2.5 px-3 rounded-xl text-sm font-medium shadow-lg hover:shadow-xl transition-all">
                        رد
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {requests.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl mx-auto mb-6 flex items-center justify-center">
              <span className="text-3xl text-gray-400">No requests</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">هیچ درخواستی یافت نشد</h3>
            <p className="text-gray-500">فیلترها را تغییر دهید یا صبر کنید تا درخواست جدیدی ثبت شود</p>
          </motion.div>
        )}
      </div>

      <style jsx>{`.font-vazir { font-family: 'Vazir', sans-serif !important; }`}</style>
    </div>
  );
}